import { API_HOST, STORAGE_KEY } from "@/lib/config"
import type { Lesson, LessonSummary } from "@/types/lesson"
import type { MicrocourseProject, MicroElement } from "@ai-tutor/shared"

const TRPC_BASE = `${API_HOST}/trpc`

async function tRPCRequest<T>(
  procedure: string,
  input?: unknown,
  method?: "GET" | "POST",
): Promise<T> {
  const token = localStorage.getItem(STORAGE_KEY)
  const headers: HeadersInit = { "Content-Type": "application/json" }
  if (token) headers["Authorization"] = `Bearer ${token}`

  let url = `${TRPC_BASE}/${procedure}`
  const fetchOptions: RequestInit = { headers }

  if (method === "GET" || (method === undefined && input === undefined)) {
    fetchOptions.method = "GET"
    if (input !== undefined) url += `?input=${encodeURIComponent(JSON.stringify(input))}`
  } else {
    fetchOptions.method = "POST"
    if (input !== undefined) fetchOptions.body = JSON.stringify(input)
  }

  const response = await fetch(url, fetchOptions)
  const data = await response.json()

  if (!response.ok) {
    const errorMessage = data?.error?.message || "请求失败"
    throw new Error(errorMessage)
  }

  return data.result.data as T
}

export async function listLessons(): Promise<LessonSummary[]> {
  return tRPCRequest("lesson.list", undefined, "GET")
}

export async function getLesson(lessonId: string): Promise<Lesson> {
  return tRPCRequest("lesson.get", { lessonId }, "GET")
}

export async function deleteLesson(lessonId: string): Promise<void> {
  return tRPCRequest("lesson.delete", { lessonId })
}

export async function updateSettings(params: {
  lessonId: string
  theme?: string
  fontFamily?: string
  transition?: string
  fontScale?: number
  customColors?: { primary?: string; accent?: string; bg?: string }
}): Promise<{ deck: MicrocourseProject }> {
  return tRPCRequest("lesson.updateSettings", params)
}

export async function generateTTS(lessonId: string): Promise<{ deck: MicrocourseProject }> {
  return tRPCRequest("lesson.generateTTS", { lessonId })
}

export async function updateSlideElement(params: {
  lessonId: string
  slideId: string
  action: "add" | "update" | "remove"
  element?: MicroElement
}): Promise<{ deck: MicrocourseProject }> {
  return tRPCRequest("lesson.updateSlideElement", params)
}

export async function createShareToken(lessonId: string): Promise<{ token: string }> {
  return tRPCRequest("lesson.createShareToken", { lessonId })
}

export async function getSharedLesson(token: string): Promise<Lesson> {
  return tRPCRequest("lesson.getShared", { token }, "GET")
}
